import { AmountDisplay } from "@/app/shared/ui/amount-display";
import { Popover, PopoverContent, PopoverTrigger } from "@/app/shared/ui/popover";
import { Check, ChevronDown, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { cn } from "../lib/utils";
import { type Account, AccountType } from "../types";

interface AccountSelectorProps {
  accounts: Account[];
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  showBalance?: boolean;
  showAllOption?: boolean;
  allOptionLabel?: string;
}

const accountTypeFilters = [
  { value: "all", label: "All" },
  { value: AccountType.BANK, label: "Bank" },
  { value: AccountType.CREDIT_CARD, label: "Card" },
  { value: AccountType.LOAN, label: "Loan" },
  { value: AccountType.DIGITAL_WALLET, label: "Wallet" },
  { value: AccountType.CRYPTOCURRENCY, label: "Crypto" },
  { value: AccountType.CASH, label: "Cash" },
  { value: AccountType.OTHER, label: "Other" },
];

export function AccountSelector({
  accounts,
  value,
  onValueChange,
  placeholder = "Select account",
  className,
  showBalance = false,
  showAllOption = false,
  allOptionLabel = "All Accounts",
}: AccountSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const selectedAccount = accounts.find((acc) => acc.id.toString() === value);

  const filteredAndSortedAccounts = useMemo(() => {
    let filtered = accounts.filter((acc) => !acc.isDeleted && !acc.isArchived);

    // Filter by type
    if (typeFilter !== "all") {
      filtered = filtered.filter((acc) => acc.type === typeFilter);
    }

    // Filter by search
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (acc) =>
          acc.accountName.toLowerCase().includes(searchLower) ||
          acc.lastFourAccountNumber?.toLowerCase().includes(searchLower),
      );
    }

    // Sort by sortOrder then accountName
    return filtered.sort((a, b) => {
      if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
        if (a.sortOrder !== b.sortOrder) {
          return a.sortOrder - b.sortOrder;
        }
      } else if (a.sortOrder !== undefined) {
        return -1;
      } else if (b.sortOrder !== undefined) {
        return 1;
      }
      return a.accountName.localeCompare(b.accountName);
    });
  }, [accounts, typeFilter, search]);

  // Filter tabs to only show types that exist in the accounts
  const availableTypes = useMemo(() => {
    const types = new Set(
      accounts.filter((acc) => !acc.isDeleted && !acc.isArchived).map((acc) => acc.type),
    );
    return accountTypeFilters.filter(
      (filter) => filter.value === "all" || types.has(filter.value as AccountType),
    );
  }, [accounts]);

  const handleSelect = (newValue: string) => {
    onValueChange(newValue);
    setOpen(false);
    setSearch("");
    setTypeFilter("all");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          // biome-ignore lint/a11y/useSemanticElements: Custom select component
          // biome-ignore lint/a11y/useAriaPropsForRole: Controlled by Popover
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-white placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-input dark:bg-foreground dark:ring-offset-background dark:placeholder:text-muted-foreground/70 dark:focus:ring-ring",
            className,
          )}
        >
          {selectedAccount ? (
            <div className="flex items-center gap-2">
              {selectedAccount.iconUrl && (
                <img
                  src={selectedAccount.iconUrl}
                  alt=""
                  className="size-5 rounded object-contain"
                />
              )}
              <span className="truncate">
                {selectedAccount.accountName}
                {selectedAccount.lastFourAccountNumber &&
                  ` - ${selectedAccount.lastFourAccountNumber}`}
              </span>
            </div>
          ) : showAllOption && (value === "none" || !value) ? (
            allOptionLabel
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronDown className="size-4 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="flex flex-col max-h-96">
          <div className="p-2 border-b border-border dark:border-input sticky top-0 bg-background dark:bg-foreground z-10">
            {/* Search Input */}
            <div className="relative mb-2">
              <Search className="absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground/70 pointer-events-none" />
              <input
                type="text"
                placeholder="Search accounts..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-md border border-input bg-background py-1.5 pl-8 pr-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring dark:border-input dark:bg-foreground dark:focus:ring-ring"
              />
            </div>

            {/* Type Filter Tabs */}
            {availableTypes.length > 2 && (
              <div className="flex flex-wrap gap-1">
                {availableTypes.map((filter) => (
                  <button
                    key={filter.value}
                    type="button"
                    onClick={() => setTypeFilter(filter.value)}
                    className={cn(
                      "rounded-md px-2 py-1 text-xs font-medium",
                      typeFilter === filter.value
                        ? "bg-foreground text-white dark:bg-muted dark:text-foreground"
                        : "bg-muted text-foreground/80 hover:bg-accent dark:bg-foreground/80 dark:text-muted-foreground/60 dark:hover:bg-foreground/60",
                    )}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="overflow-y-auto p-1">
            {showAllOption && (
              <div
                onClick={() => handleSelect("none")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleSelect("none");
                  }
                }}
                // biome-ignore lint/a11y/useSemanticElements: Custom option item
                role="button"
                tabIndex={0}
                className={cn(
                  "relative flex w-full cursor-pointer select-none items-center rounded-sm py-2 pl-8 pr-2 text-sm outline-none hover:bg-muted dark:hover:bg-foreground/80",
                  (value === "none" || !value) && "bg-muted dark:bg-foreground/80",
                )}
              >
                {(value === "none" || !value) && (
                  <span className="absolute left-2 flex size-3.5 items-center justify-center">
                    <Check className="size-4" />
                  </span>
                )}
                <span className="font-medium">{allOptionLabel}</span>
              </div>
            )}

            {filteredAndSortedAccounts.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No accounts found
              </div>
            ) : (
              filteredAndSortedAccounts.map((account) => {
                const isSelected = account.id.toString() === value;
                return (
                  <div
                    key={account.id}
                    onClick={() => handleSelect(account.id.toString())}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleSelect(account.id.toString());
                      }
                    }}
                    // biome-ignore lint/a11y/useSemanticElements: Custom option item
                    role="button"
                    tabIndex={0}
                    className={cn(
                      "relative flex w-full cursor-pointer select-none items-center rounded-sm py-2 pl-8 pr-2 text-sm outline-none hover:bg-muted dark:hover:bg-foreground/80",
                      isSelected && "bg-muted dark:bg-foreground/80",
                    )}
                  >
                    {isSelected && (
                      <span className="absolute left-2 flex size-3.5 items-center justify-center">
                        <Check className="size-4" />
                      </span>
                    )}

                    <div className="flex flex-1 items-center gap-2 min-w-0">
                      {account.iconUrl && (
                        <img
                          src={account.iconUrl}
                          alt=""
                          className="size-5 flex-shrink-0 rounded object-contain"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="truncate font-medium">{account.accountName}</span>
                          {account.lastFourAccountNumber && (
                            <span className="text-xs text-muted-foreground flex-shrink-0">
                              {account.lastFourAccountNumber}
                            </span>
                          )}
                        </div>
                        {showBalance && (
                          <div className="text-xs text-muted-foreground">
                            <AmountDisplay
                              amount={account.currentBalance}
                              currencyCode={account.currencyCode || "INR"}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
